import React, { useState, useEffect, useRef, createContext, useContext } from 'react';

/**
 * Flitship Coach Mark System v2
 *
 * Améliorations v2 :
 * 1. Copy Flitship — manifeste, pas générique
 * 2. Progression visible "1 / N" dans chaque tooltip
 * 3. Tooltip adaptatif — au-dessus si élément en bas d'écran
 * 4. Design Branding Blueprint strict
 * 5. HelpBottomSheet enrichi avec Trust Score Guide
 * 6. 4 nouveaux coach marks Profil
 */

// ─── COACH MARK DEFINITIONS ──────────────────────────────────
export const COACH_MARKS = {
  // ── Onglet Colis ──
  colis: {
    id: 'colis',
    tab: 'colis',
    order: 1,
    totalInTab: 1,
    badgeColor: '#3c6df0',
    badgeLabel: 'Colis',
    title: 'Vérifiez avant d\'envoyer',
    body: 'Chaque objet contrôlé protège tout le réseau Flitship.',
    targetTestId: 'item-check-badge-pending',
    storageKey: 'coach_colis_seen',
  },

  // ── Onglet Trajets ──
  trajets: {
    id: 'trajets',
    tab: 'trajets',
    order: 1,
    totalInTab: 1,
    badgeColor: '#f38a3e',
    badgeLabel: 'Trajets',
    title: 'Publiez votre trajet',
    body: 'Recevez des demandes compatibles dès que vous partez.',
    targetTestId: 'publish-trip-card',
    storageKey: 'coach_trajets_seen',
  },

  // ── Onglet Demandes ──
  demandes: {
    id: 'demandes',
    tab: 'demandes',
    order: 1,
    totalInTab: 1,
    badgeColor: '#3c6df0',
    badgeLabel: 'Demandes',
    title: 'Vos échanges centralisés',
    body: 'Acceptez, refusez ou suivez chaque demande ici.',
    targetTestId: 'requests-tabs',
    storageKey: 'coach_demandes_seen',
  },

  // ── Onglet Profil ──
  profile_trust: {
    id: 'profile_trust',
    tab: 'profil',
    order: 1,
    totalInTab: 4,
    badgeColor: '#4bb543',
    badgeLabel: 'Trust Score™',
    title: 'Votre score se construit ici',
    body: 'Chaque vérification et mission complétée augmente votre priorité dans le réseau.',
    targetTestId: 'trust-score-card',
    storageKey: 'coach_profile_trust_seen',
  },
  profile_identity: {
    id: 'profile_identity',
    tab: 'profil',
    order: 2,
    totalInTab: 4,
    badgeColor: '#4bb543',
    badgeLabel: 'Identité',
    title: 'Votre identité, votre clé',
    body: 'Les membres vérifiés sont prioritaires dans le matching.',
    targetTestId: 'identity-verification-section',
    storageKey: 'coach_profile_identity_seen',
  },
  profile_phone: {
    id: 'profile_phone',
    tab: 'profil',
    order: 3,
    totalInTab: 4,
    badgeColor: '#3c6df0',
    badgeLabel: 'Téléphone',
    title: '+20 points en 30 secondes',
    body: 'Vérifiez votre numéro pour renforcer la confiance dans vos échanges.',
    targetTestId: 'phone-input',
    storageKey: 'coach_profile_phone_seen',
  },
  profile_hero: {
    id: 'profile_hero',
    tab: 'profil',
    order: 4,
    totalInTab: 4,
    badgeColor: '#f38a3e',
    badgeLabel: 'Hero Status™',
    title: 'Le statut qui vous précède',
    body: 'Votre Hero Status™ apparaît sur votre profil public et dans les résultats de recherche.',
    targetTestId: 'hero-status-section',
    storageKey: 'coach_profile_hero_seen',
  },
};

// ─── TRUST SCORE GUIDE DATA ───────────────────────────────────
export const TRUST_SCORE_GUIDE = [
  { key: 'email', title: 'Email vérifié', sub: 'Compte activé', pts: '+10', done: true },
  { key: 'identity', title: 'Identité vérifiée', sub: 'Document validé par l\'équipe', pts: '+40', done: false },
  { key: 'phone', title: 'Téléphone vérifié', sub: 'OTP confirmé', pts: '+20', done: false },
  { key: 'mission', title: 'Première mission', sub: 'Complétez 1 mission', pts: '+10', done: false, next: true },
  { key: 'photo', title: 'Photo de profil', sub: 'Rendez-vous reconnaissable', pts: '+5', done: false },
  { key: 'bio', title: 'Bio complétée', sub: 'Inspirez confiance', pts: '+5', done: false },
  { key: 'ticket', title: 'Billet vérifié', sub: 'Priorité dans le matching', pts: '★', done: false },
];

// ─── CONTEXT ──────────────────────────────────────────────────
const CoachMarkContext = createContext(null);

export function CoachMarkProvider({ children }) {
  const [activeCoachMark, setActiveCoachMark] = useState(null);
  const [targetRect, setTargetRect] = useState(null);

  const showCoachMark = (markId) => {
    const mark = COACH_MARKS[markId];
    if (!mark) return;
    const target = document.querySelector(`[data-testid="${mark.targetTestId}"]`);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setTargetRect({
        top: window.innerHeight / 2 - 50,
        left: window.innerWidth / 2 - 100,
        width: 200,
        height: 80,
      });
    }
    setActiveCoachMark(mark);
  };

  const hideCoachMark = () => {
    if (activeCoachMark) localStorage.setItem(activeCoachMark.storageKey, 'true');
    setActiveCoachMark(null);
    setTargetRect(null);
  };

  const resetCoachMark = (markId) => {
    const mark = COACH_MARKS[markId];
    if (mark) localStorage.removeItem(mark.storageKey);
  };

  const hasSeenCoachMark = (markId) => {
    const mark = COACH_MARKS[markId];
    if (!mark) return true;
    return localStorage.getItem(mark.storageKey) === 'true';
  };

  return (
    <CoachMarkContext.Provider value={{ activeCoachMark, targetRect, showCoachMark, hideCoachMark, resetCoachMark, hasSeenCoachMark }}>
      {children}
      {activeCoachMark && targetRect && (
        <CoachMarkOverlay mark={activeCoachMark} targetRect={targetRect} onDismiss={hideCoachMark} />
      )}
    </CoachMarkContext.Provider>
  );
}

export function useCoachMark() {
  const context = useContext(CoachMarkContext);
  if (!context) throw new Error('useCoachMark must be used within CoachMarkProvider');
  return context;
}

// ─── OVERLAY COMPONENT ────────────────────────────────────────
function CoachMarkOverlay({ mark, targetRect, onDismiss }) {
  const padding = 8;
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, above: false });

  useEffect(() => {
    const tooltipHeight = 140;
    const screenH = window.innerHeight;
    const spaceBelow = screenH - (targetRect.top + targetRect.height);
    const above = spaceBelow < tooltipHeight + 40;

    const tooltipTop = above
      ? targetRect.top - tooltipHeight - 16
      : targetRect.top + targetRect.height + 16;

    let tooltipLeft = targetRect.left + targetRect.width / 2 - 140;
    tooltipLeft = Math.max(16, Math.min(tooltipLeft, window.innerWidth - 296));

    setTooltipPosition({ top: tooltipTop, left: tooltipLeft, above });
  }, [targetRect]);

  return (
    <div className="fixed inset-0 z-[10001]" data-testid="coach-mark-overlay">
      {/* Dark overlay with hole */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="coach-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#coach-mask)" />
      </svg>

      {/* Dismiss on click outside */}
      <div className="absolute inset-0 cursor-pointer" onClick={onDismiss} />

      {/* Highlight border */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          border: '2px solid #3c6df0',
          borderRadius: '12px',
          boxShadow: '0 0 0 4px rgba(60,109,240,0.2)',
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-white rounded-[14px] p-4 animate-in fade-in zoom-in-95 duration-300"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: '280px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          zIndex: 10002,
        }}
        data-testid="coach-mark-tooltip"
      >
        {/* Arrow */}
        <div
          className="absolute w-3 h-3 bg-white"
          style={{
            [tooltipPosition.above ? 'bottom' : 'top']: '-6px',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)',
            boxShadow: tooltipPosition.above
              ? '2px 2px 4px rgba(0,0,0,0.06)'
              : '-2px -2px 4px rgba(0,0,0,0.06)',
          }}
        />

        {/* Badge */}
        <div
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mb-2.5"
          style={{ background: mark.badgeColor + '18', border: `0.5px solid ${mark.badgeColor}30` }}
        >
          <div className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: mark.badgeColor }} />
          <span className="font-['Inter'] text-[10px] font-semibold" style={{ color: mark.badgeColor }}>
            {mark.badgeLabel}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-['Space_Grotesk'] text-[13px] font-bold text-[#1f1f1f] leading-[1.3] mb-1.5">
          {mark.title}
        </h3>

        {/* Body */}
        <p className="font-['Inter'] text-[12px] text-[#737373] leading-[1.55] mb-3">
          {mark.body}
        </p>

        {/* Footer — progress + CTA */}
        <div className="flex items-center justify-between">
          <span className="font-['Inter'] text-[10px] text-[#a3a3a3]">
            {mark.order} / {mark.totalInTab}
          </span>
          <button
            onClick={onDismiss}
            className="font-['Inter'] text-[12px] font-semibold text-[#3c6df0] hover:opacity-80 transition-opacity"
            data-testid="coach-mark-dismiss"
          >
            J'ai compris →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HELP BUTTON ──────────────────────────────────────────────
export function HelpButton({ tabId, className = '' }) {
  const { showCoachMark, resetCoachMark } = useCoachMark();
  const handleClick = () => {
    resetCoachMark(tabId);
    setTimeout(() => showCoachMark(tabId), 50);
  };
  return (
    <button
      onClick={handleClick}
      className={`w-7 h-7 rounded-full border border-[#e8e3dc] bg-white flex items-center justify-center hover:bg-[#faf6f2] transition-colors ${className}`}
      aria-label="Aide"
      data-testid={`help-button-${tabId}`}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="#a3a3a3" strokeWidth="1.2" />
        <path d="M5.5 5.5C5.5 4.4 6.4 3.5 7.5 3.5S9.5 4.4 9.5 5.5 8.4 7.5 7.5 7.5V8.5" stroke="#a3a3a3" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="7.5" cy="10.5" r="0.6" fill="#a3a3a3" />
      </svg>
    </button>
  );
}

// ─── AUTO COACH MARK HOOK ─────────────────────────────────────
export function useAutoCoachMark(tabId) {
  const { showCoachMark, hasSeenCoachMark } = useCoachMark();
  const hasTriggered = useRef(false);
  useEffect(() => {
    if (hasTriggered.current) return;
    if (!hasSeenCoachMark(tabId)) {
      const timer = setTimeout(() => {
        showCoachMark(tabId);
        hasTriggered.current = true;
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [tabId, showCoachMark, hasSeenCoachMark]);
}

// ─── TRUST SCORE GUIDE COMPONENT ──────────────────────────────
export function TrustScoreGuide({ userStats = {}, onClose, onAction }) {
  const items = TRUST_SCORE_GUIDE.map(item => ({
    ...item,
    done: userStats[item.key] || item.done,
  }));

  const completedPts = items
    .filter(i => i.done)
    .reduce((sum, i) => sum + (parseInt(i.pts) || 0), 0);

  const totalPts = 90;
  const progress = Math.min(Math.round((completedPts / totalPts) * 100), 100);

  const nextItem = items.find(i => !i.done);

  return (
    <div className="px-5 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-['Space_Grotesk'] text-[15px] font-bold text-[#1f1f1f]">
            Profil de confiance
          </h2>
          <p className="font-['Inter'] text-[11px] text-[#737373] mt-0.5">
            Complétez votre profil pour progresser
          </p>
        </div>
        <span className="font-['Space_Grotesk'] text-[20px] font-bold text-[#3c6df0]">
          {progress}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-[5px] bg-[#e8e3dc] rounded-full mb-4 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#3c6df0,#4bb543)' }}
        />
      </div>

      {/* Checklist */}
      <div className="bg-white border border-[#e8e3dc] rounded-[12px] overflow-hidden mb-4">
        {items.map((item, i) => (
          <div
            key={item.key}
            className={`flex items-center gap-3 px-4 py-3 ${i < items.length - 1 ? 'border-b border-[#f0ebe4]' : ''} ${item.next ? 'bg-[#faf8ff]' : ''}`}
          >
            {/* Status icon */}
            <div className={`w-[20px] h-[20px] rounded-full flex items-center justify-center flex-shrink-0 ${
              item.done ? 'bg-[#e8f5e8]' : item.next ? 'bg-[#eef2fd]' : 'bg-[#f5f5f5]'
            }`}>
              {item.done ? (
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                  <path d="M2 4.5l1.8 1.8 3.5-3.5" stroke="#4bb543" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              ) : item.next ? (
                <div className="w-[5px] h-[5px] rounded-full bg-[#3c6df0]" />
              ) : (
                <div className="w-[5px] h-[5px] rounded-full bg-[#d4d0cc]" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className={`font-['Inter'] text-[12px] font-semibold ${
                item.done ? 'text-[#1f1f1f]' : item.next ? 'text-[#3c6df0]' : 'text-[#a3a3a3]'
              }`}>{item.title}</div>
              <div className="font-['Inter'] text-[11px] text-[#737373]">{item.sub}</div>
            </div>

            {/* Points */}
            <span className={`font-['Space_Grotesk'] text-[11px] font-semibold ${
              item.done ? 'text-[#4bb543]' : item.next ? 'text-[#3c6df0]' : 'text-[#a3a3a3]'
            }`}>{item.pts}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      {nextItem && (
        <button
          onClick={() => onAction && onAction(nextItem.key)}
          className="w-full h-[42px] bg-[#3c6df0] text-white font-['Space_Grotesk'] font-semibold text-[13px] rounded-[12px] mb-2"
        >
          Compléter : {nextItem.title} →
        </button>
      )}
      <button
        onClick={onClose}
        className="w-full h-[38px] bg-transparent border border-[#e8e3dc] text-[#737373] font-['Inter'] text-[12px] rounded-[12px]"
      >
        Fermer
      </button>
    </div>
  );
}

// ─── HELP BOTTOM SHEET ────────────────────────────────────────
export function HelpBottomSheet({ isOpen, onClose, showTrustGuide = false, userStats = {} }) {
  const [view, setView] = useState(showTrustGuide ? 'trust' : 'main');

  if (!isOpen) return null;

  const allMarks = Object.values(COACH_MARKS).filter(m => m.order === 1);

  return (
    <>
      <div
        className="fixed inset-0 z-[10000] bg-black/30 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div
        className="fixed bottom-0 left-0 right-0 z-[10001] bg-white rounded-t-[24px] shadow-2xl animate-in slide-in-from-bottom duration-300 overflow-hidden"
        style={{ maxHeight: '80vh' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 bg-[#e8e3dc] rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3 border-b border-[#e8e3dc] flex items-center justify-between">
          {view === 'trust' ? (
            <button onClick={() => setView('main')} className="font-['Inter'] text-[13px] text-[#3c6df0]">
              ← Retour
            </button>
          ) : (
            <h2 className="font-['Space_Grotesk'] text-[15px] font-bold text-[#1f1f1f]">
              Comment ça fonctionne ?
            </h2>
          )}
          <span className="font-['Inter'] text-[12px] text-[#737373]">
            {view === 'trust' ? 'Profil de confiance' : `${Object.keys(COACH_MARKS).length} guides`}
          </span>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 100px)' }}>
          {view === 'main' ? (
            <div className="px-5 py-3">
              {allMarks.map((mark, idx) => (
                <div
                  key={mark.id}
                  className={`flex items-start gap-3 py-3.5 ${idx < allMarks.length - 1 ? 'border-b border-[#f0ebe4]' : ''}`}
                >
                  <div
                    className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                    style={{ background: mark.badgeColor + '18' }}
                  >
                    <span className="font-['Inter'] text-[11px] font-bold" style={{ color: mark.badgeColor }}>
                      {idx + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] text-[13px] font-semibold text-[#1f1f1f] mb-0.5">
                      {mark.title}
                    </h3>
                    <p className="font-['Inter'] text-[11px] text-[#737373] leading-[1.55]">
                      {mark.body}
                    </p>
                  </div>
                </div>
              ))}

              {/* Trust Score Guide CTA */}
              <div
                className="mt-3 bg-[#faf6f2] border border-[#e8e3dc] rounded-[12px] p-4 cursor-pointer"
                onClick={() => setView('trust')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-['Space_Grotesk'] text-[13px] font-semibold text-[#1f1f1f]">
                      Profil de confiance
                    </div>
                    <div className="font-['Inter'] text-[11px] text-[#737373] mt-0.5">
                      Voir ma checklist Trust Score™
                    </div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 3l4 4-4 4" stroke="#3c6df0" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full h-[42px] bg-[#3c6df0] text-white font-['Space_Grotesk'] font-semibold text-[13px] rounded-[12px] mt-4 mb-2"
              >
                Fermer
              </button>
            </div>
          ) : (
            <TrustScoreGuide userStats={userStats} onClose={onClose} />
          )}
        </div>
      </div>
    </>
  );
}

export default CoachMarkProvider;
